import type { KeyboardEvent } from 'react'
import { DeviceIconSvg, Gauge, Lock } from '../icons'
import { PowerButton } from './PowerButton'
import { formatPower, shortId } from '../lib/format'
import type { Device } from '../types/device'

interface Props {
  device: Device
  selected: boolean
  isAdmin: boolean
  onSelect: (id: string) => void
  onToggle: (id: string) => void
}

function isMetered(d: Device): boolean {
  return d.kind === 'meter-1ph' || d.kind === 'meter-3ph'
}

export function CompactTile({ device, selected, isAdmin, onSelect, onToggle }: Props) {
  const on = !!device.relay?.on && device.online
  const lockedForUser = !!device.isProtected && !isAdmin
  const state = !device.online ? 'offline' : lockedForUser ? 'locked' : on ? 'on' : 'off'
  const metered = isMetered(device)
  const hasPowerReading = device.metrics?.power != null
  const power = device.metrics?.power

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(device.id)
    }
  }

  return (
    <div
      className="ctile"
      data-state={state}
      data-on={on}
      data-selected={selected}
      onClick={() => onSelect(device.id)}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
    >
      <div className="ctile__icon">
        <DeviceIconSvg name={device.icon} />
      </div>
      <div className="ctile__text">
        <div className="ctile__name">
          {device.name}
          {device.isProtected && (
            <span className="ctile__lock" title="Riservato agli amministratori" aria-label="Riservato agli amministratori">
              <Lock />
            </span>
          )}
        </div>
        <div className="ctile__meta">
          {!device.online ? (
            <>
              <span className="ctile__dot offline"></span>
              <span className="ctile__muted">Offline</span>
            </>
          ) : lockedForUser ? (
            <>
              <span className="ctile__dot locked"></span>
              <span className="ctile__muted">Riservato amministratori</span>
            </>
          ) : metered ? (
            <>
              <span className="ctile__dot on"></span>
              <span className="ctile__power">{formatPower(power)}</span>
            </>
          ) : on ? (
            <>
              <span className="ctile__dot on"></span>
              {hasPowerReading
                ? <span className="ctile__power">{formatPower(power)}</span>
                : <span className="ctile__muted">Acceso</span>}
            </>
          ) : (
            <>
              <span className="ctile__dot off"></span>
              <span className="ctile__muted">Spento</span>
            </>
          )}
          <span className="ctile__id">{shortId(device.id)}</span>
        </div>
      </div>
      {metered ? (
        <div className="ctile__meter" aria-label="Solo misurazione">
          <Gauge />
        </div>
      ) : (
        <PowerButton
          on={on}
          disabled={!device.online || lockedForUser}
          locked={lockedForUser}
          onClick={() => onToggle(device.id)}
        />
      )}
    </div>
  )
}
